import { PAGE_CONTAINER_ID } from 'constants/layout';
import { DISCORD_SERVER_URL } from 'constants/production';
import { Subdirectory, routes } from 'constants/routing';
import { Layout } from 'containers/Layout';
import { Redirect } from 'containers/Redirect';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import { useEffect, useLayoutEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router';
import { safeLazyLoad } from 'utilities';
import { MarketRedirect } from './MarketRedirect';
import { MarketsRootRedirect } from './MarketsRootRedirect';
import PageSuspense from './PageSuspense';

const Landing = safeLazyLoad(() => import('pages/Landing'));
const IsolatedPools = safeLazyLoad(() => import('pages/IsolatedPools'));
const Markets = safeLazyLoad(() => import('pages/Markets'));
const Market = safeLazyLoad(() => import('pages/Market'));
const Dashboard = safeLazyLoad(() => import('pages/Dashboard'));
const GetStarted = safeLazyLoad(() => import('pages/GetStarted'));
const Port = safeLazyLoad(() => import('pages/Port'));
const Governance = safeLazyLoad(() => import('pages/Governance'));
const Proposal = safeLazyLoad(() => import('pages/Proposal'));
const Swap = safeLazyLoad(() => import('pages/Swap'));
const BittensorSwap = safeLazyLoad(() => import('pages/BittensorSwap'));
const Vai = safeLazyLoad(() => import('pages/Vai'));
const Vaults = safeLazyLoad(() => import('pages/Vaults'));
const Voter = safeLazyLoad(() => import('pages/Voter'));
const VoterLeaderboard = safeLazyLoad(() => import('pages/VoterLeaderboard'));
const PrimeCalculator = safeLazyLoad(() => import('pages/PrimeCalculator'));
const Bridge = safeLazyLoad(() => import('pages/Bridge'));
const Stats = safeLazyLoad(() => import('pages/Stats'));
const Skills = safeLazyLoad(() => import('pages/Skills'));
const PrivacyPolicy = safeLazyLoad(() => import('pages/PrivacyPolicy'));
const TermsOfUse = safeLazyLoad(() => import('pages/TermsOfUse'));
const Trade = safeLazyLoad(() => import('pages/Trade'));
const DevnetFaucet = safeLazyLoad(() => import('pages/DevnetFaucet'));

const AppRoutes = () => {
  const location = useLocation();
  const swapRouteEnabled = useIsFeatureEnabled({ name: 'swapRoute' });
  const bittensorSwapRouteEnabled = useIsFeatureEnabled({ name: 'bittensorSwapRoute' });
  const vaiRouteEnabled = useIsFeatureEnabled({ name: 'vaiRoute' });
  const bridgeEnabled = useIsFeatureEnabled({ name: 'bridgeRoute' });
  const tradeRouteEnabled = useIsFeatureEnabled({ name: 'trade' });
  const isolatedPoolsRouteEnabled = useIsFeatureEnabled({ name: 'isolatedPoolsRoute' });
  const vaultsRouteEnabled = useIsFeatureEnabled({ name: 'vaultsRoute' });
  const governanceRouteEnabled = useIsFeatureEnabled({ name: 'governanceRoute' });
  const portRouteEnabled = useIsFeatureEnabled({ name: 'portRoute' });
  const devnetFaucetRouteEnabled = useIsFeatureEnabled({ name: 'devnetFaucetRoute' });
  const primeCalculatorEnabled = useIsFeatureEnabled({
    name: 'primeCalculator',
  });
  const statsRouteEnabled = useIsFeatureEnabled({ name: 'statsRoute' });
  const skillsRouteEnabled = useIsFeatureEnabled({ name: 'skillsRoute' });

  // Scroll to the top of the page on route change
  // biome-ignore lint/correctness/useExhaustiveDependencies: route changes should scroll the page container back to the top
  useLayoutEffect(() => {
    document.getElementById(PAGE_CONTAINER_ID)?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }, [location.pathname]);

  useEffect(() => {
    if (window.location.pathname.startsWith('/discord')) {
      window.location.replace(DISCORD_SERVER_URL);
    }
  }, []);

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route
          index
          element={
            <PageSuspense>
              <Landing />
            </PageSuspense>
          }
        />

        <Route
          path={Subdirectory.DASHBOARD}
          element={
            <PageSuspense>
              <Dashboard />
            </PageSuspense>
          }
        />

        <Route
          path={Subdirectory.GET_STARTED}
          element={
            <PageSuspense>
              <GetStarted />
            </PageSuspense>
          }
        />

        {portRouteEnabled && (
          <Route
            path={Subdirectory.PORT}
            element={
              <PageSuspense>
                <Port />
              </PageSuspense>
            }
          />
        )}

        {isolatedPoolsRouteEnabled && (
          <Route path={Subdirectory.ISOLATED_POOLS}>
            <Route
              index
              element={
                <PageSuspense>
                  <IsolatedPools />
                </PageSuspense>
              }
            />
          </Route>
        )}

        <Route path="markets" element={<MarketsRootRedirect />} />

        <Route path={Subdirectory.MARKETS}>
          <Route
            index
            element={
              <PageSuspense>
                <Markets />
              </PageSuspense>
            }
          />

          <Route
            path={Subdirectory.MARKET}
            element={
              <PageSuspense>
                <Market />
              </PageSuspense>
            }
          />
        </Route>

        {primeCalculatorEnabled && (
          <Route
            path={Subdirectory.PRIME_CALCULATOR}
            element={
              <PageSuspense>
                <PrimeCalculator />
              </PageSuspense>
            }
          />
        )}

        {vaultsRouteEnabled && (
          <Route path={Subdirectory.VAULTS}>
            <Route
              index
              element={
                <PageSuspense>
                  <Vaults />
                </PageSuspense>
              }
            />
          </Route>
        )}

        {/* TODO: refactor to use nested routes (see VEN-2235) */}
        {governanceRouteEnabled && (
          <>
            <Route
              path={`${routes.governance.path}/*`}
              element={
                <PageSuspense>
                  <Governance />
                </PageSuspense>
              }
            />

            <Route
              path={routes.governanceLeaderBoard.path}
              element={
                <PageSuspense>
                  <VoterLeaderboard />
                </PageSuspense>
              }
            />

            <Route
              path={routes.governanceVoter.path}
              element={
                <PageSuspense>
                  <Voter />
                </PageSuspense>
              }
            />

            <Route
              path={routes.governanceProposal.path}
              element={
                <PageSuspense>
                  <Proposal />
                </PageSuspense>
              }
            />
          </>
        )}

        {tradeRouteEnabled && (
          <Route
            path={Subdirectory.TRADE}
            element={
              <PageSuspense>
                <Trade />
              </PageSuspense>
            }
          />
        )}

        {(swapRouteEnabled || bittensorSwapRouteEnabled) && (
          <Route
            path={Subdirectory.SWAP}
            element={
              <PageSuspense>
                {bittensorSwapRouteEnabled ? <BittensorSwap /> : <Swap />}
              </PageSuspense>
            }
          />
        )}

        {vaiRouteEnabled && (
          <Route
            path={Subdirectory.VAI}
            element={
              <PageSuspense>
                <Vai />
              </PageSuspense>
            }
          />
        )}

        {bridgeEnabled && (
          <Route
            path={Subdirectory.BRIDGE}
            element={
              <PageSuspense>
                <Bridge />
              </PageSuspense>
            }
          />
        )}

        {statsRouteEnabled && (
          <Route
            path={Subdirectory.STATS}
            element={
              <PageSuspense>
                <Stats />
              </PageSuspense>
            }
          />
        )}

        {devnetFaucetRouteEnabled && (
          <Route
            path={Subdirectory.DEVNET_FAUCET}
            element={
              <PageSuspense>
                <DevnetFaucet />
              </PageSuspense>
            }
          />
        )}

        {skillsRouteEnabled && (
          <Route
            path={Subdirectory.SKILLS}
            element={
              <PageSuspense>
                <Skills />
              </PageSuspense>
            }
          />
        )}

        <Route
          path={Subdirectory.PRIVACY_POLICY}
          element={
            <PageSuspense>
              <PrivacyPolicy />
            </PageSuspense>
          }
        />

        <Route
          path={Subdirectory.TERMS_OF_USE}
          element={
            <PageSuspense>
              <TermsOfUse />
            </PageSuspense>
          }
        />

        {/* Redirect old pages to new ones */}
        <Route path="/account/*" element={<Redirect to={routes.dashboard.path} />} />
        <Route path="/staking/*" element={<Redirect to={routes.vaults.path} />} />
        <Route
          path="/pool/:poolComptrollerAddress/market/:vTokenAddress"
          element={<MarketRedirect />}
        />
        <Route
          path="/pool/:poolComptrollerAddress"
          element={<Redirect to={routes.markets.path} />}
        />

        {/* Redirect to Core pool if no route matches */}
        <Route path="*" element={<Redirect to={routes.landing.path} />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
